import React, { memo, useCallback, useEffect, useState } from "react";
import { Link, useRouteMatch } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "hooks/reduxHooks";
import {
  changeSortOfExamList,
  loadMoreExams,
} from "store/PatientStore/actions";
import {
  selectExamList,
  selectHasMoreExams,
  selectSortConfig,
} from "store/PatientStore/selectors";
import { getRouteLink } from "utils/general";
import { PAGES } from "constants/pages";
import { VideoExamTableHeader } from "../../components/Athlete/VideoExamTableHeader";
import { VideoExamTableRow } from "../../components/Athlete/VideoExamTableRow";
import { ExamItem } from "types/Exam";
import Pagination from "@mui/material/Pagination";
import Box from "@mui/material/Box";
import { Loading } from "../../../../components/Loading/Loading";

export const AthleteVideoExamTable = memo(
  ({ isFreeForm }: { isFreeForm?: boolean }) => {
    const dispatch = useAppDispatch();
    const hasMoreExams = useAppSelector(selectHasMoreExams);
    const examList = useAppSelector(selectExamList);
    const sortConfig = useAppSelector(selectSortConfig);
    const match = useRouteMatch<{ id: string }>();
    const patientLimit = useAppSelector(({ patient }) => patient.limit);
    const patientOffset = useAppSelector(({ patient }) => patient.offset);
    const patientTotalExams = useAppSelector(
      ({ patient }) => patient.totalExams
    );
    const [currentPage, setCurrentPage] = useState(1);

    const loading = useAppSelector(({ patient }) => patient.loading);
    const [examListState, setExamListState] = useState<ExamItem[]>([]);

    useEffect(() => {
      const list = Array.from(examList);

      setExamListState(list.slice(0, patientLimit));
    }, [examList]);

    const onClick = (sortBy: string) => {
      dispatch(changeSortOfExamList(sortBy, match.params?.id));
    };

    const onLoadMore = useCallback(
      (ev, newPageRaw) => {
        setCurrentPage(newPageRaw);
        const list = Array.from(examList);
        const newPage = newPageRaw - 1;
        const offset = patientLimit * newPage;
        const start = offset;
        const end = offset + patientLimit;
        const result = list.slice(start, end);
        setExamListState(result);
      },
      [currentPage, examList]
    );

    const getItems = () => {
      if (!examListState) {
        return;
      }

      const examElements = [];

      let leftSDValue,
        rightSDValue,
        prevLeftSDValue,
        prevRightSDValue,
        dLeftSDValue,
        dRightSDValue,
        dPrevLeftSDValue,
        dPrevRightSDValue;

      for (let i = 0; i < examListState.length; i++) {
        const exam = examListState[i];
        const previousExam = i > 0 ? examListState[i - 1] : ({} as ExamItem);

        let linkPathname = `${getRouteLink(PAGES.videoReview)}${exam._id}/${
          exam.joint._id
        }/${exam.patientId}/${exam.joint.side}`;

        if (isFreeForm) {
          linkPathname = `${linkPathname}?isFreeForm=true`;
        }
        if (exam.joint?.side == "right") {
          rightSDValue = exam.examMLResult.rightMinValue ?? 0;
          prevRightSDValue =
            previousExam?.joint?.side == "right"
              ? previousExam?.examMLResult?.rightMinValue ?? 0
              : 0;
          leftSDValue =
            prevLeftSDValue =
            dLeftSDValue =
            dRightSDValue =
            dPrevLeftSDValue =
            dPrevRightSDValue =
              undefined;
        } else if (exam.joint?.side == "left") {
          leftSDValue = exam.examMLResult.leftMinValue ?? 0;
          prevLeftSDValue =
            previousExam?.joint?.side == "left"
              ? previousExam?.examMLResult?.leftMinValue ?? 0
              : 0;
          rightSDValue =
            prevRightSDValue =
            dLeftSDValue =
            dRightSDValue =
            dPrevLeftSDValue =
            dPrevRightSDValue =
              undefined;
        } else {
          dLeftSDValue = exam.examMLResult.leftMinValue ?? 0;
          dRightSDValue = exam.examMLResult.rightMinValue ?? 0;
          dPrevLeftSDValue =
            previousExam?.joint?.side == "left"
              ? previousExam?.examMLResult?.leftMinValue ?? 0
              : 0;
          dPrevRightSDValue =
            previousExam?.joint?.side == "right"
              ? previousExam?.examMLResult?.rightMinValue ?? 0
              : 0;
          leftSDValue =
            prevLeftSDValue =
            rightSDValue =
            prevRightSDValue =
              undefined;
        }

        const leftIsBetter = leftSDValue
          ? leftSDValue > (prevLeftSDValue ?? 0)
          : false;
        const rightIsBetter = rightSDValue
          ? rightSDValue > (prevRightSDValue ?? 0)
          : false;
        const dLeftIsBetter = dLeftSDValue
          ? dLeftSDValue > (dPrevLeftSDValue ?? 0)
          : false;
        const dRightIsBetter = dRightSDValue
          ? dRightSDValue > (dPrevRightSDValue ?? 0)
          : false;

        const bothSD: [
          { leftValue?: number; leftIsBetter?: boolean },
          { rightValue?: number; rightIsBetter?: boolean }
        ] = [
          {
            leftValue: dLeftSDValue,
            leftIsBetter: dLeftIsBetter,
          },
          {
            rightValue: dRightSDValue,
            rightIsBetter: dRightIsBetter,
          },
        ];

        const item = (
          <Link to={{ pathname: linkPathname }} key={exam.createdAt}>
            <VideoExamTableRow
              isFreeForm={isFreeForm}
              jointId={exam.joint?._id}
              type={exam.movement.name}
              leftKneeSD={{
                value: leftSDValue,
                isBetter: leftIsBetter,
              }}
              rightKneeSD={{
                value: rightSDValue,
                isBetter: rightIsBetter,
              }}
              bothKneeSD={bothSD}
              createdDate={exam.createdAt}
            />
          </Link>
        );

        examElements.push(item);
      }

      return examElements;
    };

    return (
      <div className="flex flex-col">
        <VideoExamTableHeader
          isFreeForm={isFreeForm}
          sortConfig={sortConfig}
          onClick={onClick}
        />
        <div className="w-full min-h-[200px] space-y-2 mt-3 flex flex-col overflow-y-hidden relative">
          {getItems()}
          <Loading loading={loading} states={examListState} />
        </div>
        <Box display="flex" justifyContent="flex-end" marginTop={4}>
          <Pagination
            variant="outlined"
            shape="rounded"
            page={currentPage}
            count={Math.ceil(patientTotalExams / patientLimit)}
            onChange={onLoadMore}
          />
        </Box>
      </div>
    );
  }
);
